import { useEffect, useState, useRef } from "react";
import { useUser, SignUpButton } from "@clerk/clerk-react";
import {
  ArrowRight, Linkedin, Globe, Calendar, ChevronDown, Check,
} from "lucide-react";
import Button from "../ui/Button";
import HeroHandlePicker from "../HeroHandlePicker";

// Variant C — Playful / interactive.
// Cal.com-style. Live theme switcher, rotating role word, animated tap demo.

const ROLES = ["recruiter", "salesperson", "agent", "founder", "creator", "consultant"];

const THEMES: Record<string, { name: string; bg: string; banner: string; chip: string; accent: string; ring: string }> = {
  default:    { name: "Coffee",  bg: "bg-white",                                 banner: "bg-gradient-to-br from-coffee-700 to-coffee-900",   chip: "bg-coffee-50 border-coffee-100 text-coffee-900",     accent: "text-coffee-700", ring: "ring-coffee-700" },
  midnight:   { name: "Midnight", bg: "bg-slate-950",                            banner: "bg-gradient-to-br from-slate-800 to-blue-900",      chip: "bg-slate-800/60 border-slate-700 text-slate-100",   accent: "text-blue-300",    ring: "ring-blue-400" },
  ruby:       { name: "Ruby",     bg: "bg-red-950",                              banner: "bg-gradient-to-br from-red-800 to-rose-900",        chip: "bg-red-900/60 border-red-800 text-rose-100",         accent: "text-rose-300",   ring: "ring-rose-400" },
  emerald:    { name: "Emerald",  bg: "bg-emerald-950",                          banner: "bg-gradient-to-br from-emerald-800 to-green-900",   chip: "bg-emerald-900/60 border-emerald-800 text-green-100", accent: "text-green-300", ring: "ring-green-400" },
  lavender:   { name: "Lavender", bg: "bg-gradient-to-br from-purple-100 to-violet-200", banner: "bg-gradient-to-br from-purple-500 to-violet-600", chip: "bg-white border-purple-100 text-purple-900",   accent: "text-violet-700", ring: "ring-violet-400" },
  monochrome: { name: "Mono",     bg: "bg-zinc-950",                             banner: "bg-zinc-800",                                       chip: "bg-zinc-900 border-zinc-800 text-zinc-200",          accent: "text-zinc-300",   ring: "ring-zinc-400" },
};

const FAQ = [
  { q: "Do I need an app?", a: "No. The recipient just opens the link." },
  { q: "What if I change jobs?", a: "Update the card. Every share updates with it." },
  { q: "Free tier?", a: "Yes — 1 card, 5 links, basic analytics, forever." },
  { q: "How does the NFC card work?", a: "Hold it near a phone. The card opens. Has a QR backup." },
];

export default function LandingPlayful() {
  const { isSignedIn } = useUser();
  const [roleIdx, setRoleIdx] = useState(0);
  const [themeKey, setThemeKey] = useState<keyof typeof THEMES>("default");
  const [openFAQ, setOpenFAQ] = useState<number | null>(null);
  const reduceMotion = useRef(false);

  useEffect(() => {
    document.title = "WhoBrew — One link. Share anywhere.";
    if (typeof window !== "undefined") {
      reduceMotion.current = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    }
  }, []);

  // Rotate role word every 2s. Skipped if user prefers reduced motion.
  useEffect(() => {
    if (reduceMotion.current) return;
    const interval = setInterval(() => {
      setRoleIdx((i) => (i + 1) % ROLES.length);
    }, 2200);
    return () => clearInterval(interval);
  }, []);

  const theme = THEMES[themeKey];

  return (
    <div className="bg-white text-coffee-900">
      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-dot-grid opacity-50 pointer-events-none" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 pb-20">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-7">
              <div className="eyebrow mb-5">Free · Live in 60 seconds · No card to reprint</div>

              <h1 className="text-display text-[44px] sm:text-[64px] md:text-[80px] leading-[1] tracking-[-0.02em]">
                Your link.
                <br />
                <span className="inline-flex items-baseline">
                  <span className="text-coffee-700">For the </span>
                  <span className="relative inline-block ml-3 sm:ml-4 align-baseline">
                    <span
                      key={roleIdx}
                      className="inline-block text-coffee-900 underline decoration-coffee-300 decoration-4 underline-offset-4 animate-fade-in"
                      style={{ animation: "fadeIn 400ms ease-out" }}
                    >
                      {ROLES[roleIdx]}
                    </span>
                  </span>
                  <span className="text-coffee-700">.</span>
                </span>
              </h1>

              <p className="mt-6 text-lg text-coffee-700 max-w-xl">
                One URL with your name, title, and every link people ask you for. Share by link or
                QR, edit anything, never reprint a card. Optional NFC card adds tap-to-open.
              </p>

              <div className="mt-8 max-w-xl">
                <HeroHandlePicker origin={typeof window !== "undefined" ? window.location.origin : "https://whobrew.us"} />
              </div>

              <div className="mt-5 flex flex-wrap items-center gap-x-4 gap-y-2 text-sm text-coffee-500">
                <span className="inline-flex items-center gap-1.5"><Check className="w-4 h-4 text-coffee-600" strokeWidth={3} /> No credit card</span>
                <span className="inline-flex items-center gap-1.5"><Check className="w-4 h-4 text-coffee-600" strokeWidth={3} /> No app</span>
                <span className="inline-flex items-center gap-1.5"><Check className="w-4 h-4 text-coffee-600" strokeWidth={3} /> Cancel anytime</span>
              </div>
            </div>

            {/* INTERACTIVE PREVIEW */}
            <div className="lg:col-span-5">
              <div className="relative max-w-sm mx-auto">
                <div className="absolute -inset-6 bg-coffee-100 rounded-[2.5rem] blur-2xl opacity-50" />
                <div className={`relative ${theme.bg} rounded-2xl border border-coffee-100 overflow-hidden shadow-coffee-lg transition-colors duration-300`}>
                  <div className={`h-24 ${theme.banner} transition-colors duration-300`} />
                  <div className="text-center -mt-10 px-6">
                    <div className={`w-20 h-20 rounded-full mx-auto mb-3 flex items-center justify-center ring-4 ring-white shadow ${theme.banner} transition-colors duration-300`}>
                      <span className="text-white text-xl font-bold">ST</span>
                    </div>
                    <h3 className={`text-lg font-bold ${themeKey === "default" || themeKey === "lavender" ? "text-coffee-900" : "text-white"}`}>
                      Sean Thomas
                    </h3>
                    <p className={`text-sm ${theme.accent}`}>Founder · WhoBrew</p>
                  </div>
                  <div className="px-5 pb-5 mt-4 space-y-2">
                    {[
                      { icon: <Linkedin className="w-4 h-4" />, title: "LinkedIn", desc: "Connect with me" },
                      { icon: <Globe className="w-4 h-4" />, title: "Portfolio", desc: "View my work" },
                      { icon: <Calendar className="w-4 h-4" />, title: "Book a meeting", desc: "Schedule a call" },
                    ].map((l) => (
                      <div key={l.title} className={`flex items-center gap-3 px-3 py-2.5 rounded-xl border ${theme.chip} transition-colors duration-300`}>
                        <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${themeKey === "default" || themeKey === "lavender" ? "bg-white border border-coffee-100 text-coffee-700" : "bg-white/10 border border-white/15 text-white"}`}>
                          {l.icon}
                        </div>
                        <div className="text-left">
                          <p className={`text-sm font-semibold ${themeKey === "default" || themeKey === "lavender" ? "text-coffee-900" : "text-white"}`}>{l.title}</p>
                          <p className={`text-xs opacity-70 ${themeKey === "default" || themeKey === "lavender" ? "text-coffee-500" : "text-white"}`}>{l.desc}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* THEME SWITCHER — interactive */}
              <div className="mt-5 max-w-sm mx-auto">
                <p className="text-[11px] tracking-eyebrow font-semibold text-coffee-500 mb-2.5 text-center">
                  Try a theme
                </p>
                <div className="flex justify-center gap-2">
                  {(Object.keys(THEMES) as (keyof typeof THEMES)[]).map((k) => {
                    const t = THEMES[k];
                    const active = themeKey === k;
                    return (
                      <button
                        key={k}
                        type="button"
                        onClick={() => setThemeKey(k)}
                        aria-label={`Switch to ${t.name} theme`}
                        className={`group relative ${t.banner} w-9 h-9 rounded-full border-2 ${active ? `${t.ring} ring-2 ring-offset-2 ring-offset-white border-white` : "border-white"} shadow transition-all hover:scale-110`}
                        title={t.name}
                      >
                        {active && (
                          <span className="absolute inset-0 flex items-center justify-center">
                            <Check className="w-4 h-4 text-white" strokeWidth={3} />
                          </span>
                        )}
                      </button>
                    );
                  })}
                </div>
                <p className="text-center text-[11px] text-coffee-500 mt-2">
                  11 themes total · custom colors on Pro
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* SHARE FLOW STRIP */}
      <section className="py-16 sm:py-20 border-y border-coffee-100 bg-coffee-50 overflow-hidden">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 items-center">
            <div className="text-center md:text-left">
              <div className="eyebrow mb-2">The whole flow</div>
              <h2 className="text-display text-3xl sm:text-4xl text-coffee-900 leading-tight">
                Share. Open. Done.
              </h2>
            </div>
            <div className="md:col-span-2">
              <div className="flex items-center justify-around gap-4">
                <TapStep n="01" label="Share" body="Send the link or QR" />
                <Arrow />
                <TapStep n="02" label="Open" body="Browser opens to your card" />
                <Arrow />
                <TapStep n="03" label="Save" body="They save your contact" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FEATURES */}
      <section className="py-20 sm:py-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl mb-12">
            <div className="eyebrow mb-3">What you get</div>
            <h2 className="text-display text-3xl sm:text-5xl text-coffee-900">Everything a paper card can&apos;t do.</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {[
              { title: "Always current", body: "Update once. Past shares update too." },
              { title: "11 themes", body: "Match your brand without code." },
              { title: "Visit analytics", body: "Per-link clicks, geo, devices." },
              { title: "Lead capture", body: "Visitor leaves their info + comments." },
            ].map((f) => (
              <div key={f.title} className="bg-white border border-coffee-100 rounded-2xl p-5 hover-lift">
                <h3 className="text-coffee-900 font-semibold text-base">{f.title}</h3>
                <p className="mt-1 text-sm text-coffee-600 leading-relaxed">{f.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20 sm:py-28 border-t border-coffee-100">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="eyebrow mb-3">FAQ</div>
          <h2 className="text-display text-3xl sm:text-5xl text-coffee-900 mb-8">Common questions.</h2>
          <div className="space-y-2">
            {FAQ.map((item, i) => {
              const open = openFAQ === i;
              return (
                <div key={i} className="bg-white border border-coffee-100 rounded-xl">
                  <button onClick={() => setOpenFAQ(open ? null : i)} className="w-full text-left px-5 py-4 flex items-center justify-between hover:bg-coffee-50">
                    <span className="font-semibold text-coffee-900">{item.q}</span>
                    <ChevronDown className={`w-5 h-5 text-coffee-500 transition-transform ${open ? "rotate-180" : ""}`} />
                  </button>
                  {open && <div className="px-5 pb-4 text-sm text-coffee-700 leading-relaxed">{item.a}</div>}
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* FINAL */}
      <section className="py-24 sm:py-32 bg-coffee-900 border-t border-coffee-700">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-display text-4xl sm:text-6xl text-white">Pick a name. Get a link.</h2>
          <p className="mt-4 text-coffee-200 text-lg">Sixty seconds. Free. No card to reprint.</p>
          <div className="mt-8 flex justify-center">
            {isSignedIn ? (
              <Button to="/my-card" variant="secondary" size="lg" trailingIcon={<ArrowRight className="w-4 h-4" />}>Open my card</Button>
            ) : (
              <SignUpButton mode="modal">
                <Button variant="secondary" size="lg" trailingIcon={<ArrowRight className="w-4 h-4" />}>Claim my link</Button>
              </SignUpButton>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}

function TapStep({ n, label, body }: { n: string; label: string; body: string }) {
  return (
    <div className="text-center">
      <div className="text-[10px] font-mono text-coffee-500 mb-1">{n}</div>
      <div className="text-display text-xl text-coffee-900">{label}</div>
      <p className="text-xs text-coffee-600 mt-1 max-w-[12ch] mx-auto leading-tight">{body}</p>
    </div>
  );
}

function Arrow() {
  return (
    <div className="hidden md:flex items-center text-coffee-300">
      <ArrowRight className="w-5 h-5" />
    </div>
  );
}
